import { nextTick } from 'vue'
import type { I18n, Composer, LocaleMessageObject } from 'vue-i18n'
import { setRequestLangHeaders, setHtmlLang } from './side-effects'
import enUS from '@/locales/lang/en-US.ts'
import { http } from '@/libs'
import { FALLBACK_LANG } from './langs'

// 首屏预载（同步可用）
export const PRELOADED_LOCALES: Record<string, LocaleMessageObject> = {
  en: enUS as unknown as LocaleMessageObject,
}

// 按需懒加载其它语言，避免首屏包体膨胀
export const localeFilesMap = import.meta.glob<{ default: LocaleMessageObject }>(
  '@/locales/lang/*.ts',
)

// 应用 i18n + 请求头 + <html lang>（单一出口）
export function setI18nLanguage(i18n: I18n, locale: string, messages?: LocaleMessageObject): void {
  if (messages) i18n.global.setLocaleMessage(locale, messages)
  if (i18n.mode === 'legacy') (i18n.global as any).locale = locale
  else (i18n.global as Composer).locale.value = locale
  setRequestLangHeaders(http, locale)
  setHtmlLang(locale)
}

const _inflightLocaleLoads = new Map<string, Promise<LocaleMessageObject | undefined>>()
// 加载语言资源并应用；相同语言只刷新头与 <html>
async function _loadMessagesByKey(key: string): Promise<LocaleMessageObject | undefined> {
  const loader = localeFilesMap[key]
  if (!loader) return undefined
  let p = _inflightLocaleLoads.get(key)
  if (!p) {
    p = loader()
      .then((m) => m?.default as LocaleMessageObject | undefined)
      .catch((err) => {
        console.warn('[i18n] load locale failed:', key, err)
        return undefined
      })
    _inflightLocaleLoads.set(key, p)
  }
  return p
}

// 加载语言资源并应用；相同语言仅刷新头与 <html>；支持语言级回退与失败兜底
export async function loadLocaleData(i18n: I18n, locale: string): Promise<void> {
  // 优先使用预载消息，其次按需懒加载
  const exactKey = `/src/locales/lang/${locale}.ts`
  const exactMsgs = PRELOADED_LOCALES[locale] ?? (await _loadMessagesByKey(exactKey))

  // 若精确文件不存在，尝试语言级回退（如 zh-CN -> zh）
  let msgs = exactMsgs
  if (!msgs) {
    const langOnly = locale.split('-')[0]
    if (langOnly && langOnly !== locale) {
      const langOnlyKey = `/src/locales/lang/${langOnly}.ts`
      msgs = PRELOADED_LOCALES[langOnly] ?? (await _loadMessagesByKey(langOnlyKey))
    }
  }

  // 仍无可用消息时，兜底到 FALLBACK_LANG（通常是 en）
  if (!msgs) {
    const fbKey = `/src/locales/lang/${FALLBACK_LANG}.ts`
    msgs = PRELOADED_LOCALES[FALLBACK_LANG] ?? (await _loadMessagesByKey(fbKey))
  }

  // 统一入口设置 i18n + 请求头 + <html lang>
  setI18nLanguage(i18n, locale, msgs)
}
